<template>
  <div class="draggable-window">
    <div
      v-show="hasPositioned"
      ref="draggableWindow"
      :style="computedStyles"
      class="surface safe-area scrollbar-hidden @container fixed flex h-full min-h-full w-screen resize flex-col overflow-hidden shadow-sm sm:h-auto sm:min-h-72 sm:w-auto sm:max-w-full sm:min-w-xl sm:rounded-t-lg md:min-w-2xl lg:min-w-3xl"
      :class="{ 'opacity-80': isDragging }"
      @mousedown="onContainerMouseDown"
      @resize="handleResize"
    >
      <!-- Top Bar -->
      <div class="primary w-full flex-none">
        <div ref="dragBar" class="flex cursor-move justify-end">
          <GeneralButton :icon="['fas', 'xmark']" size="large" @click.stop="close" />
        </div>
      </div>

      <!-- Window Content -->
      <div class="h-full w-full">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watchEffect, onBeforeUnmount, watch, computed } from "vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import { useWindowStore } from "@/store/windows.store"
import { useUserSettingsStore } from "@/store/user-settings.store"
import { storeToRefs } from "pinia"
import { useEventListener } from "@vueuse/core"
import "@interactjs/auto-start"
import "@interactjs/actions/resize"
import interact from "@interactjs/interact"
import { useDeviceDetection } from "@/composables/utils/device-detection"

const props = defineProps<{
  id: number
}>()

const emit = defineEmits(["close", "loaded"])

const windowStore = useWindowStore()
const userSettingsStore = useUserSettingsStore()

const { userSettings } = storeToRefs(userSettingsStore)
const { isSmallMq } = useDeviceDetection()

const draggableWindow = ref()
const dragBar = ref()

const x = ref(0)
const y = ref(0)
const zIndex = ref(windowStore.nextZIndex())
const windowHeight = ref(window.innerHeight)
const windowElementWidth = ref(0)

const isDragging = ref(false)
const hasPositioned = ref(false)
let resizeTimeout: ReturnType<typeof setTimeout> | undefined

const lastMousePosition = { x: 0, y: 0 }

// The child should call this function when it is ready (e.g. images loaded), so that the window can be centered.
provide("onChildLoaded", onChildLoaded)
provide("getDraggableWindow", getDraggableWindow)
provide("close", close)

const thisWindow = computed(() => windowStore.getWindow(props.id)!)
provide("window", thisWindow)

function moveToPosition(xPos: number, yPos: number) {
  if (yPos < 0) {
    yPos = 0
  }
  x.value = xPos
  y.value = yPos

  thisWindow.value.lastPositionX = xPos
  thisWindow.value.lastPositionY = yPos
}

function onMouseDown(event: MouseEvent) {
  event.preventDefault() // Disable text selection
  isDragging.value = true

  const rect = draggableWindow.value.getBoundingClientRect()
  windowHeight.value = rect.height
  windowElementWidth.value = rect.width

  lastMousePosition.x = event.clientX
  lastMousePosition.y = event.clientY
}

function onMouseMove(event: MouseEvent) {
  if (isDragging.value) {
    const deltaX = event.clientX - lastMousePosition.x
    const deltaY = event.clientY - lastMousePosition.y

    const margin = 100

    y.value = calculateNewY(y.value, deltaY, margin)
    x.value = calculateNewX(x.value, deltaX, windowElementWidth.value, margin)

    lastMousePosition.x = event.clientX
    lastMousePosition.y = event.clientY
  }
}

function calculateNewY(currentY: number, deltaY: number, margin: number) {
  const newY = currentY + deltaY
  const constrainedTop = newY > 0 ? newY : 0
  const borderBottom = window.innerHeight - margin
  return constrainedTop < borderBottom ? constrainedTop : borderBottom
}

function calculateNewX(currentX: number, deltaX: number, elementWidth: number, margin: number) {
  const newX = currentX + deltaX
  const borderRight = window.innerWidth - margin
  const constrainedRight = newX < borderRight ? newX : borderRight
  const borderLeft = 0 - elementWidth + margin
  return constrainedRight > borderLeft ? constrainedRight : borderLeft
}

function onMouseUp() {
  isDragging.value = false
}

function handleResize() {
  if (!draggableWindow.value) {
    return
  }
  clearTimeout(resizeTimeout)
  if (isSmallMq.value) {
    draggableWindow.value.classList.remove("pointer-events-none")
    draggableWindow.value.style.transform = ""
    Object.assign(draggableWindow.value.dataset, { x: "", y: "" })
    return
  }

  draggableWindow.value.classList.add("pointer-events-none")
  const rect = draggableWindow.value.getBoundingClientRect()
  windowHeight.value = rect.height
  windowElementWidth.value = rect.width

  // Move to center if window is outside viewport
  if (
    rect.right < 0 ||
    rect.right > window.innerWidth ||
    rect.bottom < 0 ||
    rect.left > window.innerWidth ||
    rect.top > window.innerHeight
  ) {
    moveToPosition(
      (window.innerWidth - windowElementWidth.value) / 2,
      (window.innerHeight - windowHeight.value) / 2
    )
  }

  resizeTimeout = setTimeout(() => {
    draggableWindow.value?.classList.remove("pointer-events-none")
  }, 500)
}

function onChildLoaded() {
  const screenBoundsUpperThreshold = 0.95

  if (!hasPositioned.value && userSettings.value) {
    const rect = draggableWindow.value.getBoundingClientRect()
    windowHeight.value = rect.height
    windowElementWidth.value = rect.width

    if (
      userSettings.value.windowPosX >= 0 &&
      userSettings.value.windowPosX < screenBoundsUpperThreshold &&
      userSettings.value.windowPosY >= 0 &&
      userSettings.value.windowPosY < screenBoundsUpperThreshold
    ) {
      // Position window based on user settings
      moveToPosition(
        userSettings.value.windowPosX * window.innerWidth,
        userSettings.value.windowPosY * window.innerHeight
      )
    } else {
      // No user settings defined, center the window
      moveToPosition(
        (window.innerWidth - windowElementWidth.value) / 2,
        (window.innerHeight - windowHeight.value) / 2
      )
    }

    for (const window of windowStore.windows) {
      if (
        window.id !== props.id &&
        x.value === window.lastPositionX &&
        y.value === window.lastPositionY
      ) {
        moveToPosition(x.value + 20, y.value + 20)
      }
    }

    hasPositioned.value = true
    // Let parent know component will become visible.
    emit("loaded", props.id)
  }
}

function onContainerMouseDown() {
  zIndex.value = windowStore.nextZIndex()
}

function getDraggableWindow() {
  return draggableWindow.value
}

function close() {
  emit("close", props.id)
}

const computedStyles = computed(() => {
  const isSmallScreen = isSmallMq.value
  const mediaQueryStyles = isSmallScreen
    ? { top: 0, left: 0 }
    : { top: `${y.value}px`, left: `${x.value}px` }

  return {
    zIndex: zIndex.value,
    width: !isSmallScreen && windowElementWidth.value ? `${windowElementWidth.value}px` : undefined,
    height: !isSmallScreen && windowHeight.value ? `${windowHeight.value}px` : undefined,
    ...mediaQueryStyles
  }
})

const resizeObserver = new ResizeObserver(handleResize)

useEventListener(dragBar, "mousedown", onMouseDown)
useEventListener(window, "mousemove", onMouseMove)
useEventListener(window, "mouseup", onMouseUp)
useEventListener(window, "resize", handleResize)

watchEffect(() => {
  const elem = dragBar.value
  if (elem) {
    resizeObserver.observe(draggableWindow!.value)
    // resize all sides
    interact(draggableWindow!.value).resizable({
      margin: 4,
      edges: { top: true, left: true, bottom: true, right: true },
      listeners: {
        move: function (event) {
          let { x, y } = event.target.dataset

          x = (parseFloat(x) || 0) + event.deltaRect.left
          y = (parseFloat(y) || 0) + event.deltaRect.top

          Object.assign(event.target.style, {
            width: `${event.rect.width}px`,
            height: `${event.rect.height}px`,
            transform: `translate(${x}px, ${y}px)`
          })

          Object.assign(event.target.dataset, { x, y })
        }
      }
    })
  }
})

watch(isDragging, (val) => {
  if (!val && userSettings.value) {
    userSettings.value.windowPosX = x.value / window.innerWidth
    userSettings.value.windowPosY = y.value / window.innerHeight
    userSettingsStore.saveUserSettings()
  }
})

onBeforeUnmount(() => {
  clearTimeout(resizeTimeout)
  resizeObserver.disconnect()
  if (draggableWindow.value) {
    interact(draggableWindow.value).unset()
  }
})
</script>

<style scoped lang="scss">
.scrollbar-hidden {
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
